/* eslint-disable react/no-unstable-nested-components */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Loader from 'components/common/Loader';
import { FSBTable } from 'components/common/FSBTable';
import { axiosGetAPI } from 'services';
import { authContext } from 'contexts/AuthContext';
import { RxCross2 } from 'react-icons/rx';
import { useDispatch } from 'react-redux';
import { toggleModal } from 'actions/orderDetailsAction';
import { useQuery } from '@apollo/client';
import { GET_ORDER_BY_ID } from 'pages/Orders/OrdersGraphQL';

const columns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },
  { accessorKey: 'orderId', header: 'Order ID', enableSorting: false },
  {
    accessorKey: 'sellerAddress',
    header: "Seller's Address",
    enableSorting: false,
    cell: ({ row }) => {
      return row?.original?.sellerAddress ? (
        <p> {row?.original?.sellerAddress} </p>
      ) : (
        <p>-</p>
      );
    },
  },
  {
    accessorKey: 'buyerAddress',
    header: "Buyer's Address",
    enableSorting: false,
    cell: ({ row }) => {
      return row?.original?.buyerAddress ? (
        <p> {row?.original?.buyerAddress} </p>
      ) : (
        <p>-</p>
      );
    },
  },
  {
    accessorKey: 'quantity',
    header: 'Order Quantity',
    enableSorting: false,
    cell: ({ row }) => {
      return row?.original?.quantityUnit ? (
        <p>
          {' '}
          {row?.original?.quantity} {row?.original?.quantityUnit}
        </p>
      ) : (
        <p>0</p>
      );
    },
  },
  {
    accessorKey: 'message',
    header: '',
    enableSorting: false,
    cell: ({ row }) => {
      return row?.original?.status === 'FREIGHT' ? (
        <p>Buyer awaiting freight charges</p>
      ) : (
        <p>User uploaded payment proof</p>
      );
    },
  },
  {
    accessorKey: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) =>
      row?.original?.status === 'FREIGHT' ? (
        <div>
          <Button color="primary" style={{ textWrap: 'nowrap' }}>
            {' '}
            Update Charges{' '}
          </Button>
        </div>
      ) : (
        <div>
          <Button style={{ textWrap: 'nowrap' }} color="primary">
            {' '}
            Review & Approve{' '}
          </Button>
        </div>
      ),
  },
];

const PendingOrdersWrapper = (props) => {
  const {
    toggleAlert,
    open = false,
    onActionClick: setOrderDataToRow,
    title,
  } = props;

  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pendingOrders, setPendingOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const { auth } = useContext(authContext);
  const dispatch = useDispatch();

  const { data: orderData, loading: orderDataLoading } = useQuery(
    GET_ORDER_BY_ID,
    {
      variables: {
        id: orderId,
      },
      skip: !orderId,
      fetchPolicy: 'network',
    },
  );

  useEffect(() => {
    if (!orderDataLoading && orderData) {
      toggleAlert();
      setOrderDataToRow(orderData?.order);
    }
  }, [orderDataLoading, orderData, setOrderDataToRow, toggleAlert]);

  const fetchPendingOrders = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}order/pending-action-orders?pageSize=${pageSize}&pageNum=${pageNum}`,
        auth,
      );
      setPendingOrders(resp?.data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [auth, pageNum, pageSize]);

  // eslint-disable-next-line no-shadow
  const handlePageChange = ({ pageSize, pageNo }) => {
    setPendingOrders(null);
    setPageSize(pageSize);
    setPageNum(pageNo - 1);
  };

  useEffect(() => {
    fetchPendingOrders();
  }, [fetchPendingOrders]);

  const onClose = () => {
    dispatch(toggleModal({ showForm: false }));
  };

  const showSequenceNumber = (cell, filters) => {
    const index =
      (cell?.row?.index ?? 0) +
      1 +
      (filters?.pageNum ?? 1) * (filters?.pageSize ?? 10);
    return index;
  };

  const onActionClick = ({ row }) => {
    setOrderId(row?.original?.orderId);
  };

  const actionsFormatter = (cell) =>
    cell?.row?.original?.status === 'FREIGHT' ? (
      <div>
        <Button
          color="primary"
          style={{ textWrap: 'nowrap', fontSize: '12px' }}
          onClick={() => onActionClick(cell)}
        >
          {orderDataLoading ? 'Please Wait...' : 'Update Charges'}{' '}
        </Button>
      </div>
    ) : (
      <div>
        <Button
          style={{ textWrap: 'nowrap', fontSize: '12px' }}
          color="primary"
          onClick={() => onActionClick(cell)}
        >
          {' '}
          {orderDataLoading ? 'Please Wait...' : 'Update Charges'}{' '}
        </Button>
      </div>
    );

  const linkFormatter = (cell) => {
    return (
      <div
        className="text-blue-c5 fw-medium cursor-pointer"
        onClick={() => onActionClick(cell)}
        aria-hidden
      >
        {cell?.row?.original?.orderId}
      </div>
    );
  };

  const findIndexOfItem = (item) => {
    const findIndex = columns.findIndex((itm) => itm.accessorKey === item);
    return findIndex;
  };

  columns[findIndexOfItem('SNo')].cell = (cell) =>
    showSequenceNumber(cell, { pageNum, pageSize });

  columns[findIndexOfItem('actions')].cell = actionsFormatter;
  columns[findIndexOfItem('orderId')].cell = linkFormatter;

  return (
    <Modal size="lg" centered isOpen={open} toggle={toggleAlert}>
      <ModalBody style={{ padding: '20px' }}>
        <div className="d-flex justify-content-between">
          <h4
            style={{
              fontWeight: '600',
              size: '18px',
              color: '#3D454D',
              overflow: 'hidden',
            }}
          >
            {title}
          </h4>
          <RxCross2 size={12} style={{ marginTop: '-2px' }} onClick={onClose} />
        </div>
        <FSBTable
          columns={columns}
          data={(pendingOrders && pendingOrders?.content) || []}
          totalCount={(pendingOrders && pendingOrders?.totalElements) || 0}
          totalPages={(pendingOrders && pendingOrders?.totalPages) || 0}
          currentPageNo={pageNum + 1}
          pageSize={pageSize}
          keyField="id"
          showSearch={false}
          showFilters
          showDelete={false}
          showPagination
          remote
          tableClass="action-table order-table"
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break "
          handlePageChange={handlePageChange}
          globalSearchStr=""
          onGlobalSearch={() => {}}
          onSortChange={() => {}}
          searchPlaceholder="Search Orders"
          loading={loading}
        >
          {/* Only can see add and delete buttons as Only admin can add or delete users */}
        </FSBTable>
      </ModalBody>
    </Modal>
  );
};

export default PendingOrdersWrapper;
